import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BookOpen,
  CalendarClock,
  ChefHat,
  ClipboardList,
  Dumbbell,
  Eye,
  EyeOff,
  FileText,
  GraduationCap,
  Image as ImageIcon,
  Kanban,
  Layers,
  ListChecks,
  Loader2,
  Music,
  Package,
  PenTool,
  Plus,
  Rows3,
  Shapes,
  Stethoscope,
  Trash2,
  Users,
  Wallet,
  Wand2,
  X,
} from 'lucide-react';
import type { CanonBiblia, ConfigCaderno, EstiloCaligrafia, EstiloEscrita, Formato, ImagemCaderno, TipoCaderno } from '@miolos/core';
import {
  CADERNOS_AUTOMATICOS,
  CANONS_BIBLIA,
  DESCRICAO_CADERNO,
  DESCRICAO_CANON,
  ESTILOS_CALIGRAFIA,
  ESTILOS_ESCRITA,
  ROTULO_CADERNO,
  ROTULO_CANON,
  ROTULO_ESCRITA,
  ROTULO_ESTILO_CALIGRAFIA,
  ROTULO_TRACO,
  TIPOS_CADERNO,
  TRACOS_LINHA,
  criarCadernoPadrao,
  medirPaginaDoMiolo,
  resumoDoCanon,
  totalDeFolhas,
  type EstiloTraco,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch, Slider } from '@/components/ui/switch';
import { api, urlDaImagem } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { useEditor } from '../EstadoEditor';
import { TituloPasso } from '../ui';

const ICONES: Record<TipoCaderno, typeof BookOpen> = {
  CONTATOS: Layers,
  SENHAS: Eye,
  EMERGENCIA: Shapes,
  ORACOES: BookOpen,
  LEITURA_BIBLIA: BookOpen,
  PAUTADO: PenTool,
  CALIGRAFIA: PenTool,
  PARTITURA: Music,
  DESENHO: ImageIcon,
  RECEITAS: ChefHat,
  FINANCAS: Wallet,
  HABITOS: Shapes,
  TREINO: Dumbbell,
  ESTUDOS: GraduationCap,
  CONVIDADOS: Users,
  PLANEJAMENTO: ListChecks,
  HORARIO: CalendarClock,
  REUNIOES: ClipboardList,
  PROJETOS: Kanban,
  PACIENTES: Stethoscope,
  ORCAMENTOS: FileText,
  ESTOQUE: Package,
  PROMPTS: Wand2,
  FOLHAS: Rows3,
};

function medirImagem(url: string): Promise<number | null> {
  return new Promise((resolver) => {
    const imagem = new Image();
    imagem.onload = () => resolver(imagem.naturalHeight > 0 ? imagem.naturalWidth / imagem.naturalHeight : null);
    imagem.onerror = () => resolver(null);
    imagem.src = url;
  });
}

export function PassoCadernos() {
  const { config, atualizar, documento } = useEditor();
  const { usuario } = useAuth();
  const [selecionado, setSelecionado] = useState<string | null>(null);
  const [mostrarTipos, setMostrarTipos] = useState(false);

  /**
   * O caderno de prompts de IA é um bônus dado pelo administrador do sistema:
   * quem não tem não vê a opção (e a API também recusa salvar, caso a
   * configuração chegue por outro caminho).
   */
  const tiposDisponiveis = useMemo(
    () =>
      TIPOS_CADERNO.filter(
        (tipo: TipoCaderno) => tipo !== 'PROMPTS' || usuario?.promptsIA || usuario?.admin,
      ),
    [usuario?.promptsIA, usuario?.admin],
  );

  const cadernos = config.cadernos;
  const aberto = cadernos.find((caderno) => caderno.id === selecionado) ?? null;

  const atualizarCaderno = useCallback(
    (id: string, alteracoes: Partial<ConfigCaderno>) => {
      atualizar({
        cadernos: cadernos.map((caderno) => (caderno.id === id ? { ...caderno, ...alteracoes } : caderno)),
      });
    },
    [cadernos, atualizar],
  );

  const adicionar = useCallback(
    (tipo: TipoCaderno) => {
      const novo = criarCadernoPadrao(tipo, config.formato);
      atualizar({ cadernos: [...cadernos, novo] });
      setSelecionado(novo.id);
      setMostrarTipos(false);
    },
    [cadernos, config.formato, atualizar],
  );

  const remover = useCallback(
    (id: string) => {
      atualizar({ cadernos: cadernos.filter((caderno) => caderno.id !== id) });
      if (selecionado === id) setSelecionado(null);
    },
    [cadernos, atualizar, selecionado],
  );

  const totalPaginas = documento.resumo.paginasDeCaderno;

  return (
    <div className="space-y-6">
      <TituloPasso
        titulo="Cadernos extras"
        descricao="Adicione quantos cadernos quiser ao fim do miolo: contatos, senhas, emergência, orações, plano de leitura da Bíblia, pautado, caligrafia, pauta musical, desenho, receitas, gastos, hábitos, treino e estudos."
      />

      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3">
        <Button onClick={() => setMostrarTipos((valor) => !valor)}>
          <Plus />
          Adicionar caderno
        </Button>
        <Badge tom="principal">{cadernos.length} caderno(s)</Badge>
        <Badge>{totalPaginas} páginas de caderno</Badge>
        <span className="ml-auto text-xs text-muted-foreground">
          Total do miolo: {documento.resumo.totalPaginas} páginas
        </span>
      </div>

      {mostrarTipos ? (
        <section className="grid gap-3 lg:grid-cols-2 xl:grid-cols-3">
          {tiposDisponiveis.map((tipo) => {
            const Icone = ICONES[tipo];
            return (
              <button
                key={tipo}
                type="button"
                onClick={() => adicionar(tipo)}
                className="rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-primary/50"
              >
                <div className="flex items-center gap-2">
                  <span className="flex size-8 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icone className="size-4" />
                  </span>
                  <span className="text-sm font-semibold">{ROTULO_CADERNO[tipo]}</span>
                  {CADERNOS_AUTOMATICOS.includes(tipo) ? (
                    <Badge className="ml-auto text-[10px]">auto</Badge>
                  ) : null}
                  {tipo === 'PROMPTS' ? (
                    <Badge tom="destaque" className="ml-auto text-[10px]">
                      bônus
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{DESCRICAO_CADERNO[tipo]}</p>
              </button>
            );
          })}
        </section>
      ) : null}

      {cadernos.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/40 p-8 text-center">
          <Shapes className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Nenhum caderno ainda</p>
          <p className="mx-auto mt-1 max-w-lg text-xs leading-relaxed text-muted-foreground">
            Cadernos são seções extras com layout próprio. Você escolhe o tipo, o número de páginas e, no caso do
            caderno de desenho, envia as imagens que vão compor as páginas.
          </p>
        </div>
      ) : (
        <div className="grid gap-6 xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
          <ul className="space-y-2">
            {cadernos.map((caderno) => {
              const Icone = ICONES[caderno.tipo];
              return (
                <li key={caderno.id}>
                  <div
                    className={[
                      'flex items-center gap-2 rounded-lg border p-3 transition-colors',
                      aberto?.id === caderno.id
                        ? 'border-primary bg-primary/[0.04]'
                        : 'border-border bg-card hover:border-primary/40',
                    ].join(' ')}
                  >
                    <button
                      type="button"
                      onClick={() => setSelecionado(caderno.id)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                        <Icone className="size-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{caderno.titulo}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {ROTULO_CADERNO[caderno.tipo]}
                          {CADERNOS_AUTOMATICOS.includes(caderno.tipo)
                            ? ' · páginas automáticas'
                            : ` · ${caderno.paginas} pág.`}
                        </span>
                      </span>
                    </button>
                    <button
                      type="button"
                      title={caderno.visivel ? 'Ocultar caderno' : 'Mostrar caderno'}
                      aria-label={caderno.visivel ? 'Ocultar caderno' : 'Mostrar caderno'}
                      onClick={() => atualizarCaderno(caderno.id, { visivel: !caderno.visivel })}
                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                    >
                      {caderno.visivel ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                    </button>
                    <button
                      type="button"
                      title="Remover caderno"
                      aria-label="Remover caderno"
                      onClick={() => remover(caderno.id)}
                      className="rounded p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-destructive"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {aberto ? (
            <ConfiguracaoCaderno
              caderno={aberto}
              formato={config.formato}
              temConta={Boolean(usuario)}
              aoAtualizar={(alteracoes) => atualizarCaderno(aberto.id, alteracoes)}
            />
          ) : (
            <p className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              Escolha um caderno na lista para configurar.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Configuração do caderno selecionado                                 */
/* ------------------------------------------------------------------ */

function ConfiguracaoCaderno({
  caderno,
  formato,
  temConta,
  aoAtualizar,
}: {
  caderno: ConfigCaderno;
  formato: Formato;
  temConta: boolean;
  aoAtualizar: (alteracoes: Partial<ConfigCaderno>) => void;
}) {
  // No caderno de folhas o total de páginas vem da soma dos blocos.
  const atualizarFolhas = (blocos: ConfigCaderno['blocosFolhas']) =>
    aoAtualizar({ blocosFolhas: blocos, paginas: totalDeFolhas(blocos) });

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Título do caderno" dica="Aparece no topo de cada página e no índice.">
          <Input
            value={caderno.titulo}
            onChange={(evento) => aoAtualizar({ titulo: evento.target.value })}
            maxLength={70}
          />
        </Campo>

        {CADERNOS_AUTOMATICOS.includes(caderno.tipo) ? (
          <Campo rotulo="Páginas" dica="Calculado automaticamente conforme o conteúdo.">
            <Input value="automático" disabled />
          </Campo>
        ) : caderno.tipo === 'FOLHAS' ? (
          <Campo rotulo="Páginas" dica="Somadas dos blocos logo abaixo.">
            <Input value={`${totalDeFolhas(caderno.blocosFolhas)} (pelos blocos)`} disabled />
          </Campo>
        ) : (
          <Campo rotulo="Quantidade de páginas" dica="Você pode aumentar depois sem perder o resto.">
            <Input
              type="number"
              min={1}
              max={120}
              value={caderno.paginas}
              onChange={(evento) => aoAtualizar({ paginas: Math.max(1, Number(evento.target.value) || 1) })}
            />
          </Campo>
        )}
      </div>

      {caderno.tipo === 'PAUTADO' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Estilo da pauta">
            <Select
              value={caderno.estiloPauta}
              onValueChange={(valor) => aoAtualizar({ estiloPauta: valor as EstiloEscrita })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTILOS_ESCRITA.map((estilo) => (
                  <SelectItem key={estilo} value={estilo}>
                    {ROTULO_ESCRITA[estilo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
          <div className="space-y-2 self-end">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Altura da linha</span>
              <span className="font-mono text-xs text-muted-foreground">{caderno.alturaLinhaMm} mm</span>
            </div>
            <Slider
              value={[caderno.alturaLinhaMm]}
              min={4}
              max={12}
              step={0.5}
              onValueChange={([valor]) => aoAtualizar({ alturaLinhaMm: valor ?? 7 })}
            />
          </div>
          <LinhaSwitch
            id={`data-${caderno.id}`}
            rotulo="Campo de data e assunto no topo"
            checked={caderno.mostrarData}
            onCheckedChange={(valor) => aoAtualizar({ mostrarData: valor })}
          />
        </div>
      ) : null}

      {caderno.tipo === 'CALIGRAFIA' ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo rotulo="Tipo de guia">
            <Select
              value={caderno.estiloCaligrafia}
              onValueChange={(valor) => aoAtualizar({ estiloCaligrafia: valor as EstiloCaligrafia })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ESTILOS_CALIGRAFIA.map((estilo) => (
                  <SelectItem key={estilo} value={estilo}>
                    {ROTULO_ESTILO_CALIGRAFIA[estilo]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
          <div className="space-y-2 self-end">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Altura da linha</span>
              <span className="font-mono text-xs text-muted-foreground">
                {caderno.alturaLinhaCaligrafiaMm} mm
              </span>
            </div>
            <Slider
              value={[caderno.alturaLinhaCaligrafiaMm]}
              min={10}
              max={26}
              step={1}
              onValueChange={([valor]) => aoAtualizar({ alturaLinhaCaligrafiaMm: valor ?? 16 })}
            />
          </div>
        </div>
      ) : null}

      {caderno.tipo === 'PARTITURA' ? (
        <p className="rounded-md bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
          Os pentagramas são distribuídos automaticamente: em {formato} cabem{' '}
          <strong className="text-foreground">
            {Math.max(3, Math.floor((medirPaginaDoMiolo({ formato }).alturaConteudoMm - 15) / (8 + 7)))}
          </strong>{' '}
          sistemas por página, com barra inicial e final. Escreva a clave e a fórmula de compasso à mão.
        </p>
      ) : null}

      {caderno.tipo === 'LEITURA_BIBLIA' ? (
        <div className="space-y-4">
          <Campo
            rotulo="Cânon"
            dica="As versões diferem: a católica tem 7 livros e 145 capítulos a mais que a evangélica."
          >
            <Select
              value={caderno.canonBiblia}
              onValueChange={(valor) => aoAtualizar({ canonBiblia: valor as CanonBiblia })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CANONS_BIBLIA.map((canon) => (
                  <SelectItem key={canon} value={canon}>
                    {ROTULO_CANON[canon]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>

          <p className="rounded-md bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
            {DESCRICAO_CANON[caderno.canonBiblia]} Neste plano: <strong>{resumoDoCanon(caderno.canonBiblia)}</strong>.
          </p>

          <LinhaSwitch
            id={`abrev-${caderno.id}`}
            rotulo="Usar abreviações dos livros"
            descricao="Recomendado em A5 e A6: “Gn 1–3” ocupa bem menos espaço que “Gênesis 1–3”."
            checked={caderno.abreviarLivros}
            onCheckedChange={(valor) => aoAtualizar({ abreviarLivros: valor })}
          />
        </div>
      ) : null}

      {caderno.tipo === 'FOLHAS' ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">Blocos de folhas</p>
              <p className="text-xs leading-relaxed text-muted-foreground">
                Monte o caderno do seu jeito: quantas páginas pautadas, quantas pontilhadas, quantas com linha
                tracejada. O total aparece no fim.
              </p>
            </div>
            <Badge tom="principal">
              {totalDeFolhas(caderno.blocosFolhas)} páginas no caderno
            </Badge>
          </div>

          <ul className="space-y-3">
            {caderno.blocosFolhas.map((bloco, indice) => (
              <li key={indice} className="rounded-lg border border-border bg-card p-3">
                <div className="grid gap-3 sm:grid-cols-[1.2fr_1fr_1.4fr_auto] sm:items-end">
                  <Campo rotulo={`Bloco ${indice + 1} — estilo`}>
                    <Select
                      value={bloco.estilo}
                      onValueChange={(valor) =>
                        atualizarFolhas(
                          caderno.blocosFolhas.map((item, i) =>
                            i === indice
                              ? {
                                  ...item,
                                  estilo: valor as EstiloEscrita,
                                  traco:
                                    valor === 'PONTILHADO'
                                      ? 'PONTILHADA'
                                      : valor === 'LINHA'
                                        ? item.traco ?? 'SOLIDA'
                                        : undefined,
                                }
                              : item,
                          ),
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESTILOS_ESCRITA.map((estilo) => (
                          <SelectItem key={estilo} value={estilo}>
                            {ROTULO_ESCRITA[estilo]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Campo>

                  <Campo rotulo="Traço" dica={bloco.estilo === 'LINHA' ? 'Vale para linhas.' : 'Só para linhas.'}>
                    <Select
                      value={bloco.traco ?? 'SOLIDA'}
                      disabled={bloco.estilo !== 'LINHA' && bloco.estilo !== 'QUADRICULADO'}
                      onValueChange={(valor) =>
                        atualizarFolhas(
                          caderno.blocosFolhas.map((item, i) =>
                            i === indice ? { ...item, traco: valor as EstiloTraco } : item,
                          ),
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TRACOS_LINHA.map((traco) => (
                          <SelectItem key={traco} value={traco}>
                            {ROTULO_TRACO[traco]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Campo>

                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">Páginas deste bloco</span>
                      <span className="font-mono text-muted-foreground">{bloco.paginas}</span>
                    </div>
                    <Slider
                      value={[bloco.paginas]}
                      min={1}
                      max={80}
                      step={1}
                      onValueChange={([valor]) =>
                        atualizarFolhas(
                          caderno.blocosFolhas.map((item, i) =>
                            i === indice ? { ...item, paginas: valor ?? item.paginas } : item,
                          ),
                        )
                      }
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {[5, 10, 20, 30, 50].map((quantidade) => (
                        <button
                          key={quantidade}
                          type="button"
                          onClick={() =>
                            atualizarFolhas(
                              caderno.blocosFolhas.map((item, i) =>
                                i === indice ? { ...item, paginas: quantidade } : item,
                              ),
                            )
                          }
                          className={[
                            'rounded-full border px-2 py-0.5 text-[11px] transition-colors',
                            bloco.paginas === quantidade
                              ? 'border-primary bg-primary/10 text-primary'
                              : 'border-border text-muted-foreground hover:border-primary/40',
                          ].join(' ')}
                        >
                          {quantidade}
                        </button>
                      ))}
                    </div>
                  </div>

                  <Button
                    variant="fantasma"
                    tamanho="icone"
                    aria-label="Remover bloco"
                    disabled={caderno.blocosFolhas.length <= 1}
                    onClick={() =>
                      atualizarFolhas(caderno.blocosFolhas.filter((_, i) => i !== indice))
                    }
                  >
                    <Trash2 />
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="contorno"
              tamanho="pequeno"
              onClick={() =>
                atualizarFolhas([
                  ...caderno.blocosFolhas,
                  { estilo: 'LINHA', traco: 'TRACEJADA', paginas: 10, rotulo: 'Tracejada' },
                ])
              }
            >
              <Plus />
              Adicionar bloco
            </Button>
            <span className="text-xs text-muted-foreground">
              Resumo:{' '}
              {caderno.blocosFolhas
                .map(
                  (bloco) =>
                    `${bloco.paginas} ${ROTULO_ESCRITA[bloco.estilo].toLowerCase()}${
                      bloco.estilo === 'LINHA' && bloco.traco && bloco.traco !== 'SOLIDA'
                        ? ` (${ROTULO_TRACO[bloco.traco].toLowerCase()})`
                        : ''
                    }`,
                )
                .join(' + ')}
            </span>
          </div>
        </div>
      ) : null}

      {caderno.tipo === 'PLANEJAMENTO' || caderno.tipo === 'REUNIOES' ? (
        <Campo
          rotulo={caderno.tipo === 'REUNIOES' ? 'Itens da pauta já impressos' : 'Tarefas já impressas'}
          dica="Uma por linha. Elas saem impressas nas primeiras linhas do checklist; o resto fica em branco."
        >
          <Textarea
            value={caderno.itens.join('\n')}
            onChange={(evento) =>
              aoAtualizar({
                itens: evento.target.value
                  .split('\n')
                  .map((linha) => linha.trim())
                  .filter(Boolean)
                  .slice(0, 80),
              })
            }
            rows={9}
            placeholder={'Definir data, estilo e orçamento\nEscolher e reservar o local'}
          />
        </Campo>
      ) : null}

      {caderno.tipo === 'HORARIO' ? (
        <div className="space-y-4">
          <Campo
            rotulo="Horários da grade"
            dica="Uma linha por horário. Viram a primeira coluna da tabela."
          >
            <Textarea
              value={caderno.itens.join('\n')}
              onChange={(evento) =>
                aoAtualizar({
                  itens: evento.target.value
                    .split('\n')
                    .map((linha) => linha.trim())
                    .filter(Boolean)
                    .slice(0, 20),
                })
              }
              rows={5}
              placeholder={'07:00\n08:00\n09:00'}
            />
          </Campo>

          <Campo rotulo="Dias da semana" dica="Separe por vírgula.">
            <Input
              value={caderno.diasDaGrade.join(', ')}
              onChange={(evento) =>
                aoAtualizar({
                  diasDaGrade: evento.target.value
                    .split(',')
                    .map((dia) => dia.trim())
                    .filter(Boolean)
                    .slice(0, 7),
                })
              }
            />
          </Campo>

          <Campo rotulo="Matérias" dica="Uma por linha. Saem impressas na tabela de matérias e professores.">
            <Textarea
              value={caderno.materias.join('\n')}
              onChange={(evento) =>
                aoAtualizar({
                  materias: evento.target.value
                    .split('\n')
                    .map((linha) => linha.trim())
                    .filter(Boolean)
                    .slice(0, 30),
                })
              }
              rows={5}
              placeholder={'Português\nMatemática\nCiências'}
            />
          </Campo>
        </div>
      ) : null}

      {caderno.tipo === 'DESENHO' ? (
        <GaleriaDoCaderno caderno={caderno} temConta={temConta} aoAtualizar={aoAtualizar} />
      ) : null}

      <p className="text-xs leading-relaxed text-muted-foreground">
        {DESCRICAO_CADERNO[caderno.tipo]}
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Caderno de desenho: envio de imagens                                */
/* ------------------------------------------------------------------ */

function GaleriaDoCaderno({
  caderno,
  temConta,
  aoAtualizar,
}: {
  caderno: ConfigCaderno;
  temConta: boolean;
  aoAtualizar: (alteracoes: Partial<ConfigCaderno>) => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const entrada = useRef<HTMLInputElement>(null);

  const imagens = caderno.imagens;

  const enviar = useCallback(
    async (arquivos: FileList) => {
      setErro(null);
      setEnviando(true);
      try {
        const novas: ImagemCaderno[] = [];
        for (const arquivo of Array.from(arquivos)) {
          const resposta = await api.enviarImagem(arquivo);
          const proporcao = await medirImagem(urlDaImagem(resposta.arquivo.url));
          novas.push({
            id: `img-${Math.random().toString(36).slice(2, 10)}`,
            url: resposta.arquivo.url,
            nome: resposta.arquivo.nome,
            legenda: '',
            proporcao,
          });
        }
        aoAtualizar({ imagens: [...imagens, ...novas] });
      } catch (falha) {
        setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar as imagens.');
      } finally {
        setEnviando(false);
      }
    },
    [imagens, aoAtualizar],
  );

  return (
    <div className="space-y-4 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={entrada}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={(evento) => {
            if (evento.target.files?.length) void enviar(evento.target.files);
            evento.target.value = '';
          }}
        />
        <Button variant="contorno" disabled={!temConta || enviando} onClick={() => entrada.current?.click()}>
          {enviando ? <Loader2 className="animate-spin" /> : <ImageIcon />}
          {enviando ? 'Enviando...' : 'Enviar imagens'}
        </Button>
        <span className="text-xs text-muted-foreground">
          Cada imagem ocupa uma página. Você pode enviar várias de uma vez.
        </span>
      </div>

      {erro ? <p className="text-xs text-destructive">{erro}</p> : null}

      {imagens.length > 0 ? (
        <ul className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {imagens.map((imagem, indice) => (
            <li key={imagem.id} className="space-y-1.5">
              <span className="relative block">
                <img
                  src={urlDaImagem(imagem.url)}
                  alt={imagem.nome}
                  className="aspect-square w-full rounded-md border border-border object-cover"
                />
                <span className="absolute left-1 top-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-bold text-white">
                  {indice + 1}
                </span>
              </span>
              <Input
                value={imagem.legenda}
                placeholder="Legenda (opcional)"
                onChange={(evento) =>
                  aoAtualizar({
                    imagens: imagens.map((item) =>
                      item.id === imagem.id ? { ...item, legenda: evento.target.value } : item,
                    ),
                  })
                }
                className="h-7 text-xs"
              />
              <div className="flex gap-1">
                <Button
                  variant="fantasma"
                  tamanho="pequeno"
                  className="flex-1"
                  disabled={indice === 0}
                  onClick={() => {
                    const copia = [...imagens];
                    [copia[indice - 1], copia[indice]] = [copia[indice]!, copia[indice - 1]!];
                    aoAtualizar({ imagens: copia });
                  }}
                >
                  ← mover
                </Button>
                <Button
                  variant="fantasma"
                  tamanho="icone"
                  aria-label="Remover imagem"
                  onClick={() => aoAtualizar({ imagens: imagens.filter((item) => item.id !== imagem.id) })}
                >
                  <X />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">
          Nenhuma imagem ainda. Sem imagens, o caderno sai apenas com páginas em branco.
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Páginas em branco no fim" dica="Para você desenhar à mão depois das imagens.">
          <Input
            type="number"
            min={0}
            max={80}
            value={caderno.paginasEmBranco}
            onChange={(evento) =>
              aoAtualizar({ paginasEmBranco: Math.max(0, Number(evento.target.value) || 0) })
            }
          />
        </Campo>
        <div className="self-end">
          <LinhaSwitch
            id={`moldura-${caderno.id}`}
            rotulo="Moldura nas páginas em branco"
            checked={caderno.molduraDesenho}
            onCheckedChange={(valor) => aoAtualizar({ molduraDesenho: valor })}
          />
        </div>
      </div>
    </div>
  );
}
